import marketingPreferencesClient from 'rpc/marketingPreferencesClient';
import {
  RetrieveMarketingPreferencesRequest,
  RetrieveMarketingPreferencesResponse,
  UpdateMarketingPreferenceRequest,
  UpdateMarketingPreferenceResponse,
  MarketingOptInSetting,
} from 'rpc/model/squareup/buyerportal/preference/marketing';
import { RequestStatus } from 'rpc/model/squareup/customers/request';
import {
  MarketingPreferencesData,
  MarketingPreferencesConverter,
} from 'routes/profile/models/MarketingPreferences';
import { callApi, getResponseError } from '../utils';
import { buyerportalApi } from '..';

const MARKETING_PREFERENCES_PAGE_SIZE = 100;

const enhancedApi = buyerportalApi.enhanceEndpoints({
  addTagTypes: ['MarketingPreferences'],
});

export const marketingPreferencesApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({
    retrieveMarketingPreferences: builder.query<
      MarketingPreferencesData,
      string
    >({
      providesTags: ['MarketingPreferences'],
      async queryFn(emailId) {
        let allMerchantPreferences: MarketingPreferencesData['merchantPreferences'] =
          [];
        let totalMerchants = 0;
        let globalSetting = MarketingOptInSetting.MARKETING_OPT_IN;
        let hasMore = true;

        while (hasMore) {
          const response = await callApi<RetrieveMarketingPreferencesResponse>(
            () =>
              marketingPreferencesClient.retrieveMarketingPreferences(
                RetrieveMarketingPreferencesRequest.create({
                  emailId,
                  limit: MARKETING_PREFERENCES_PAGE_SIZE,
                  offset: allMerchantPreferences.length,
                })
              )
          );

          const { data } = response;
          if (data?.status !== RequestStatus.STATUS_SUCCESS) {
            return getResponseError(data);
          }

          globalSetting =
            data.globalSetting ?? MarketingOptInSetting.MARKETING_OPT_IN;
          totalMerchants = data.totalMerchants ?? 0;

          const merchantPrefs = (data.merchantPreferences ?? []).map(
            MarketingPreferencesConverter.fromMerchantPreference
          );

          allMerchantPreferences = [
            ...allMerchantPreferences,
            ...merchantPrefs,
          ];

          hasMore =
            merchantPrefs.length >= MARKETING_PREFERENCES_PAGE_SIZE &&
            allMerchantPreferences.length < totalMerchants;
        }

        return {
          data: {
            globalSetting,
            merchantPreferences: allMerchantPreferences,
            totalMerchants,
          },
        };
      },
    }),
    updateMarketingPreference: builder.mutation<
      null,
      {
        copyId?: string;
        emailId: string;
        merchantId?: string;
        setting: MarketingOptInSetting;
      }
    >({
      async onQueryStarted(
        { emailId, merchantId, setting },
        { dispatch, queryFulfilled }
      ) {
        const updateResult = dispatch(
          marketingPreferencesApi.util.updateQueryData(
            'retrieveMarketingPreferences',
            emailId,
            (draft) => {
              if (merchantId) {
                const merchantPref = draft.merchantPreferences.find(
                  (p) => p.merchantId === merchantId
                );
                if (merchantPref) {
                  merchantPref.setting = setting;
                }
              } else {
                // eslint-disable-next-line no-param-reassign
                draft.globalSetting = setting;
              }
            }
          )
        );

        try {
          await queryFulfilled;
          // Opting back in restores the buyer's per-merchant preferences on
          // the backend, so refetch to pick them up — but only after the
          // update has landed. Invalidating while the update is still in
          // flight kicks off a refetch that returns the stale opted-out
          // state and clobbers the optimistic update above.
          if (
            !merchantId &&
            setting === MarketingOptInSetting.MARKETING_OPT_IN
          ) {
            dispatch(enhancedApi.util.invalidateTags(['MarketingPreferences']));
          }
        } catch {
          updateResult.undo();
        }
      },
      async queryFn({ emailId, copyId, merchantId, setting }) {
        const response = await callApi<UpdateMarketingPreferenceResponse>(() =>
          marketingPreferencesClient.updateMarketingPreference(
            UpdateMarketingPreferenceRequest.create({
              copyId,
              emailId,
              merchantId,
              setting,
            })
          )
        );

        const { data } = response;
        if (data?.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(data);
        }

        return { data: null };
      },
    }),
  }),
});

export const {
  useRetrieveMarketingPreferencesQuery,
  useUpdateMarketingPreferenceMutation,
} = marketingPreferencesApi;
